import { useEffect, useRef, useState } from "react";
import { homeData } from "../../../mocks/homeData";
import type { FeatureItem } from "../../../api";

interface Props {
  data?: { tag?: string; title?: string; items?: FeatureItem[] } | null;
}

export default function FeaturesSection({ data: apiData }: Props) {
  // apiData 可能返回空 items，此时回退到 mock 数据
  const features = (apiData && apiData.items && apiData.items.length > 0) ? apiData : homeData.features;
  const sectionRef = useRef<HTMLDivElement>(null);
  const [animated, setAnimated] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setAnimated(true);
          observer.disconnect();
        }
      },
      { threshold: 0.2 }
    );
    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }
    return () => observer.disconnect();
  }, []);

  return (
    <section ref={sectionRef} className="bg-light-gray py-16 md:py-24 px-4 md:px-8 lg:px-12">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-6 mb-12">
          <div className="flex-1">
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-dark leading-tight">
              Built for{" "}
              <span className="text-primary">Performance</span> in Every{" "}
              <span className="text-secondary">Environment</span>
            </h2>
          </div>
          <div className="flex-shrink-0">
            <span className="inline-flex items-center px-4 py-1.5 bg-dark text-white text-sm rounded-full">
              {features.tag}
            </span>
          </div>
        </div>

        {/* Features Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
          {features.items.map((feature, index) => (
            <div
              key={index}
              className={`bg-white rounded-xl overflow-hidden transition-all duration-700 ${
                animated
                  ? "opacity-100 translate-y-0"
                  : "opacity-0 translate-y-6"
              }`}
              style={{ transitionDelay: `${index * 150}ms` }}
            >
              <div className="aspect-[4/3] overflow-hidden bg-gradient-to-br from-primary/10 to-secondary/10 flex items-center justify-center">
                {feature.image ? (
                  <img
                    src={feature.image}
                    alt={feature.title}
                    className="w-full h-full object-cover object-top"
                  />
                ) : (
                  <i className={`${feature.icon || 'ri-shield-check-line'} text-5xl text-primary/30`}></i>
                )}
              </div>
              <div className="p-4">
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1">
                    <h3 className="text-dark font-semibold text-base mb-1">
                      {feature.title}
                    </h3>
                    <p className="text-gray-500 text-sm leading-relaxed">
                      {feature.description}
                    </p>
                  </div>
                  <div className="text-2xl font-bold text-primary flex-shrink-0">
                    {feature.metric}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}